import { FunctionComponent } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import type { ManagementCapaDayPoint } from "../utils/management-capa-chart.utils";
import {
  getManagementChartLegendProps,
  getManagementChartTooltipProps
} from "../utils/management-chart-theme.utils";
import {
  formatManagementChartTooltipValue,
  getManagementPeriodChartYAxisProps
} from "../utils/management-display-unit.utils";
import { ManagementChartEmptyState } from "./ManagementChartEmptyState";

type ManagementCapaDayChartProps = {
  data: ManagementCapaDayPoint[];
  primarySeriesName?: string;
  compareSeriesName?: string;
  /** Idle / empty copy when `data` has no points. */
  emptyMessageKey?: string;
};

/**
 * Capacity-over-days line chart for the Management location detail page.
 */
export const ManagementCapaDayChart: FunctionComponent<ManagementCapaDayChartProps> = ({
  data,
  primarySeriesName,
  compareSeriesName,
  emptyMessageKey = "_management_capaIdle"
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tooltipProps = getManagementChartTooltipProps(theme);
  const legendProps = getManagementChartLegendProps(theme);
  const yAxisProps = getManagementPeriodChartYAxisProps("percent");
  const primaryName = primarySeriesName ?? t("Occupancy");
  const showCompare = Boolean(compareSeriesName);

  return (
    <Box data-testid="management-capa-day-chart" sx={{ width: "100%", height: 280 }}>
      <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
        {t("_management_capaPerDay")}
      </Typography>
      {data.length === 0 ? (
        <ManagementChartEmptyState messageKey={emptyMessageKey} />
      ) : (
        <ResponsiveContainer width="100%" height="90%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis dataKey="dayLabel" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
            <YAxis
              domain={yAxisProps.domain}
              allowDecimals={yAxisProps.allowDecimals}
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              tickFormatter={yAxisProps.tickFormatter}
            />
            <Tooltip
              contentStyle={tooltipProps.contentStyle}
              itemStyle={tooltipProps.itemStyle}
              labelStyle={tooltipProps.labelStyle}
              formatter={(value: number) => formatManagementChartTooltipValue(value, "percent")}
            />
            {showCompare && (
              <Legend wrapperStyle={legendProps.wrapperStyle} formatter={legendProps.formatter} />
            )}
            <Line
              type="monotone"
              dataKey="display"
              name={primaryName}
              stroke={theme.palette.primary.main}
              strokeWidth={2}
              dot={false}
            />
            {showCompare && (
              <Line
                type="monotone"
                dataKey="displayCompare"
                name={compareSeriesName}
                stroke={theme.palette.warning.main}
                strokeWidth={2}
                dot={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};
